import React, { useState, useContext, useRef, useEffect } from 'react';

import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';

import { POST_ITEMS_PER_PAGE } from '../../../apis/khoros/constants';
import {
  useKhorosEditPost,
  useKhorosPost,
  useKhorosUser,
} from '../../../apis/khoros/hooks';
import { KhorosReplyItemSchema } from '../../../apis/khoros/type/khoros-replies.type';
import { useUserProfile } from '../../../apis/useUserSearch';
import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import I18nContext from '../../../aurr/contexts/I18nContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import { ToastContext } from '../../../aurr/contexts/ToastContext';
import { ProfileAvatar } from '../../container/user';

interface CommentFormProps {
  baseURL?: string;
  comment?: KhorosReplyItemSchema;
  userProfileURL?: string;
  userContentURL?: string;
  onEditTentative?: () => void;
  onEditSuccess?: () => void;
  onEditFail?: () => void;
  topicId: string;
}

export default function PostForm({
  baseURL,
  comment,
  userProfileURL,
  userContentURL,
  onEditTentative,
  onEditSuccess,
  onEditFail,
  topicId,
}: CommentFormProps) {
  const { userProfile } = useContext(LoginContext);
  const { setErrorToast } = useContext(ToastContext);
  const { kh_body, kh_post_id } = comment || {};

  const { t } = useContext(I18nContext);
  const { USER_PROFILE_URL, USER_CONTENT_URL, KHOROS_API } =
    useContext(ConfigContext);

  const parsedBody = kh_body
    ?.replace(/(\\r)|(\\n)/g, '\n')
    ?.replace(/(<([^>]+)>)/gi, '');

  const [isEditing, setIsEditing] = useState(false);
  const [isActive, setIsActive] = useState(Boolean(kh_body && kh_body.length));
  const [newComment, setNewComment] = useState(parsedBody || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const topicProps = {
    baseURL: baseURL || KHOROS_API,
    topicId,
  };

  const {
    mutateAsync: onReplyTopic,
    isPending: replyTopicLoading,
    isError: replyTopicIsError,
    reset: replyTopicReset,
  } = useKhorosPost({ ...topicProps, pageSize: POST_ITEMS_PER_PAGE });

  const {
    mutateAsync: onCommentUpdate,
    isPending: updateCommentLoading,
    isError: updateCommentIsError,
    reset: updateCommentReset,
  } = useKhorosEditPost({
    ...topicProps,
    commentId: kh_post_id,
    pageSize: POST_ITEMS_PER_PAGE,
  });

  const isLoading = replyTopicLoading || updateCommentLoading;
  const isError = replyTopicIsError || updateCommentIsError;

  const { data: user } = useUserProfile({
    oxygenId: userProfile?.sub || '',
    userProfileURL: userProfileURL || USER_PROFILE_URL,
    userContentURL: userContentURL || USER_CONTENT_URL,
  });

  const { firstName, lastName } = user || {};

  const { data: khorosUserData, isLoading: isKhorosUserLoading } =
    useKhorosUser({
      baseURL: baseURL || KHOROS_API,
    });

  const parsedName =
    firstName && lastName
      ? `${firstName} ${lastName}`
      : khorosUserData?.kh_user_login || '';

  const handleFocus = () => {
    setIsActive(true);
  };

  const handleBlur = () => {
    setIsActive(Boolean(newComment && newComment.length));
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value || '';
    setNewComment(value);
  };

  useEffect(() => {
    const textareaEl =
      textareaRef.current?.querySelector<HTMLTextAreaElement>('textarea[name]');
    if (isActive && textareaEl && document.activeElement !== textareaEl) {
      const cursorPosition = textareaEl.value.length + 7;
      textareaEl.focus();
      textareaEl.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [isActive]);

  const uniqueId = `commentForm-${kh_post_id || 'new'}-${Math.random()}`;

  const handleCloseError = () => {
    replyTopicReset();
    updateCommentReset();
  };

  const handleClick = async () => {
    handleCloseError();

    if (newComment?.trim() === '') {
      setIsActive(false);
      onEditSuccess?.();
      return;
    }

    if (newComment && kh_post_id) {
      onEditTentative?.();
      setIsEditing(true);
      setIsActive(false);
      try {
        await onCommentUpdate(newComment);
        onEditSuccess?.();
        return;
      } catch (e) {
        setErrorToast({
          message: t('comments.postError', { ns: 'common' }),
        });
        setIsEditing(false);
        setIsActive(true);
        onEditFail?.();
        return;
      }
    }

    if (newComment) {
      try {
        setNewComment('');
        setIsActive(false);
        await onReplyTopic(newComment);
      } catch (e) {
        setErrorToast({
          message: t('comments.postError', { ns: 'common' }),
        });
        setNewComment(newComment);
        setIsActive(true);
      }
    }
  };

  return isEditing ? null : (
    <div className="au-flex au-gap-2">
      <ProfileAvatar
        alt={t('avatarFor', { ns: 'common', fullName: parsedName })}
        size="medium"
        src={khorosUserData?.kh_user_avatar}
        fullName={parsedName}
        isLoading={isKhorosUserLoading}
      />
      <div className="au-grow">
        <FormControl
          fullWidth
          sx={{
            marginBottom: '12px',
          }}
        >
          <InputLabel
            htmlFor={uniqueId}
            sx={{
              color: 'white',
              '&.Mui-focused': {
                color: 'white !important',
              },
            }}
          >
            {t('comments.comment', { ns: 'common' })}
          </InputLabel>
          <Input
            ref={textareaRef}
            id={uniqueId}
            multiline
            rows={isActive ? 4 : 1}
            disabled={isLoading}
            error={isError}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            name="commentForm"
            value={newComment}
            placeholder={t('comments.CommentOnClass', { ns: 'common' })}
            sx={{
              '& .MuiInputBase-input': {
                color: 'white !important',
                border: '1px solid white',
                '&::placeholder': {
                  color: 'white !important',
                  opacity: 1,
                },
              },
            }}
          />
        </FormControl>
        {isActive ? (
          <div className="au-flex au-justify-between">
            <div className="au-flex au-flex-col au-text-c12 au-text-gray-300">
              <span>{t('comments.beNicePolicy', { ns: 'common' })}</span>
              <span>{t('comments.bePositive', { ns: 'common' })}</span>
            </div>
            <button
              type="button"
              className="au-uppercase au-bg-[#FEFF09] au-text-black au-py-1 au-px-3 au-rounded-sm au-text-c12"
              disabled={!newComment || newComment.length === 0 || isLoading}
              onClick={() => handleClick()}
            >
              {t('comments.post', { ns: 'common' })}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
